import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, FileText, Mail, MessageSquare, Phone, Star } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { fmtDate } from "@web/lib/format";
import { venuesQuery } from "@web/lib/queries";
import {
  vendorBookingsQuery, vendorCategoriesQuery, vendorDirectoryApi, vendorQuery,
} from "@web/lib/queries-vendors";
import { VENDOR_COI_MAX_BYTES } from "@shared/schemas/vendors";
import {
  BottomSheet, Button, Card, EmptyState, FilePicker, Inline, Picker, Row, RowGroup, SelectChip,
  Skeleton, Stack, StatusChip, Text, TextField, useToast,
} from "@web/ui";
import { coiLabel, coiTone } from "./VendorRow";

/** One vendor, everything about them (SPEC §6.1 item 13a).
 *
 * Contact rows are tap-to-call, tap-to-text and tap-to-email, because the
 * commonest reason anybody opens this on a phone is that they need the florist
 * on the line. Below that: the certificate and when it runs out, which venues
 * prefer them, and the events they have worked.
 *
 * Editing is inline and one field at a time rather than a form with a Save
 * button, so a correction never sits unsaved behind a sheet somebody closes.
 */
export function VendorSheet({ vendorId, onClose }: { vendorId: string; onClose: () => void }) {
  const toast = useToast();
  const vendor = useQuery(vendorQuery(vendorId));
  const bookings = useQuery(vendorBookingsQuery(vendorId));
  const categories = useQuery(vendorCategoriesQuery);
  const venues = useQuery(venuesQuery());
  const [expiresOn, setExpiresOn] = useState("");
  const [busy, setBusy] = useState(false);
  const row = vendor.data;

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    try { await action(); toast.success(done); }
    catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  async function upload(file: File) {
    if (!expiresOn) { toast.error("Add the date the certificate runs out first."); return; }
    await run(() => vendorDirectoryApi.uploadCoi(vendorId, file, expiresOn), "Certificate saved");
    setExpiresOn("");
  }

  return (
    <BottomSheet open onClose={onClose} size="large" title={row?.name ?? "Vendor"}>
      {vendor.isError
        ? <EmptyState icon={FileText} title="This vendor could not be loaded" body={errorMessage(vendor.error)}
            action={<Button onClick={() => void vendor.refetch()}>Try again</Button>} />
        : !row ? <Skeleton count={4} />
          : <Stack gap={4}>
            <Inline gap={2} wrap align="center">
              <StatusChip tone={coiTone(row.coiStatus)}>{coiLabel(row.coiStatus)}</StatusChip>
              {row.categoryName && <StatusChip tone="neutral">{row.categoryName}</StatusChip>}
              {row.active === 0 && <StatusChip tone="warn">Retired</StatusChip>}
            </Inline>

            <Card>
              <Stack gap={2}>
                <Text as="h3" variant="title">How to reach them</Text>
                <RowGroup>
                  {row.contactName && <Row title={row.contactName} subtitle="Who you deal with" wrap />}
                  {row.phone && <Row icon={<Phone aria-hidden size={18} />} title={row.phone} subtitle="Tap to call" wrap
                    onClick={() => window.location.assign(`tel:${row.phone}`)}
                    actions={<Button variant="secondary" fullWidth={false} icon={<MessageSquare aria-hidden size={18} />}
                      href={`sms:${row.phone}`}>Text</Button>} />}
                  {row.email && <Row icon={<Mail aria-hidden size={18} />} title={row.email} subtitle="Tap to email" wrap
                    onClick={() => window.location.assign(`mailto:${row.email}`)} />}
                  {row.website && <Row title={row.website} subtitle="Website" wrap
                    onClick={() => window.open(row.website!.startsWith("http") ? row.website! : `https://${row.website}`, "_blank", "noopener")} />}
                  {row.instagram && <Row title={row.instagram} subtitle="Instagram" wrap />}
                  {row.address && <Row title={row.address} subtitle="Address" wrap />}
                </RowGroup>
                {!row.phone && !row.email && !row.website && (
                  <Text variant="caption" tone="secondary">Nobody can reach this vendor from a booking yet. Add a phone number or an email.</Text>
                )}
              </Stack>
            </Card>

            <Card>
              <Stack gap={2}>
                <Text as="h3" variant="title">Certificate of insurance</Text>
                {row.coiStatus === "none"
                  ? <Text variant="callout" tone="secondary">No certificate on file. Bookings this vendor is on will keep asking for one.</Text>
                  : <Text variant="callout" tone={row.coiStatus === "expired" ? "danger" : "secondary"}>
                    {row.coiStatus === "expired"
                      ? `Expired${row.coiExpiresOn ? ` on ${fmtDate(row.coiExpiresOn)}` : ""}. Bookings this vendor is on are waiting for a new one.`
                      : `On file until ${row.coiExpiresOn ? fmtDate(row.coiExpiresOn) : "an unknown date"}.`}
                  </Text>}
                <TextField label="Runs out on" type="date" value={expiresOn} onChange={event => setExpiresOn(event.target.value)}
                  hint="We need this to know when to ask for the next one." />
                <FilePicker label={row.coiStatus === "none" ? "Upload the certificate" : "Replace the certificate"}
                  accept="application/pdf,image/jpeg,image/png" maxBytes={VENDOR_COI_MAX_BYTES}
                  disabled={busy || !expiresOn} loading={busy}
                  onTooLarge={() => toast.error("That file is over 10 MB. Try a photo or a smaller scan.")}
                  onPick={file => void upload(file)} />
                {row.coiDocKey && (
                  <Button variant="secondary" fullWidth={false} icon={<FileText aria-hidden size={18} />}
                    href={`/api/vendors/${row.id}/coi`}>Open the certificate</Button>
                )}
              </Stack>
            </Card>

            <Card>
              <Stack gap={2}>
                <Text as="h3" variant="title">Preferred at</Text>
                <Text variant="caption" tone="secondary">
                  Preferred vendors come first in the picker on a booking at that venue. Everybody else is still available.
                </Text>
                <Inline gap={2} wrap>
                  {(venues.data ?? []).map(venue => {
                    const preferred = row.preferredVenueIds.includes(venue.id);
                    return (
                      <SelectChip key={venue.id} selected={preferred} disabled={busy}
                        onClick={() => void run(
                          () => vendorDirectoryApi.setPreference(row.id, { venueId: venue.id, preferred: !preferred }),
                          preferred ? `No longer preferred at ${venue.shortName ?? venue.name}` : `Preferred at ${venue.shortName ?? venue.name}`)}>
                        {preferred && <Star aria-hidden size={14} />}
                        {venue.shortName ?? venue.name}
                      </SelectChip>
                    );
                  })}
                </Inline>
              </Stack>
            </Card>

            <Card>
              <Stack gap={2}>
                <Text as="h3" variant="title">Details</Text>
                <Picker label="Category" value={row.categoryId ?? ""} disabled={busy}
                  onChange={value => void run(() => vendorDirectoryApi.update(row.id, { categoryId: value || null }), "Category saved")}
                  options={[{ value: "", label: "No category" }, ...(categories.data ?? []).map(category => ({ value: category.id, label: category.name }))]} />
                <Button variant="secondary" fullWidth={false} disabled={busy}
                  onClick={() => void run(() => vendorDirectoryApi.update(row.id, { active: row.active === 1 ? 0 : 1 }),
                    row.active === 1 ? "Retired from the directory" : "Back in the directory")}>
                  {row.active === 1 ? "Retire from the directory" : "Put back in the directory"}
                </Button>
                <Text variant="caption" tone="tertiary">
                  Retiring keeps this vendor on the bookings they already worked. They simply stop being offered for new ones.
                </Text>
              </Stack>
            </Card>

            <Card>
              <Stack gap={2}>
                <Text as="h3" variant="title">Events they have worked</Text>
                {!bookings.data ? <Skeleton count={2} />
                  : bookings.data.length === 0
                    ? <Text variant="callout" tone="secondary">No bookings yet.</Text>
                    : <RowGroup>
                      {bookings.data.map(booking => (
                        <Row key={booking.bookingId} wrap
                          icon={<CalendarDays aria-hidden size={18} />}
                          title={booking.eventName || fmtDate(booking.eventDate)}
                          subtitle={[booking.venueName, fmtDate(booking.eventDate), booking.role].filter(Boolean).join(" · ")}
                          trailing={<StatusChip tone={booking.coiStatus === "received" ? "success" : "warn"}>{booking.coiStatus}</StatusChip>} />
                      ))}
                    </RowGroup>}
              </Stack>
            </Card>
          </Stack>}
    </BottomSheet>
  );
}
